// src/components/TrustDeviceModal/TrustDeviceModal.js
import React, { useState, useEffect } from 'react';
import './TrustDeviceModal.css';
import { FaFingerprint, FaShieldAlt, FaMobileAlt } from 'react-icons/fa';
import Button from './buttons/Button';
import { biometricService } from '../services';
import { createLogger } from '../services/Debug';
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';

const TrustDeviceModal = ({ isOpen, onClose, onTrust, onSkip }) => {
  const [deviceName, setDeviceName] = useState('Mon appareil');
  const [placeholder, setPlaceholder] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('biométrique');
  const log = createLogger('TrustDeviceModal');
  
  useEffect(() => {
    const checkBiometric = async () => {
      const available = await biometricService.isAvailable();
      setBiometricAvailable(available);
      
      if (available) {
        const types = await biometricService.getAvailableTypes();
        if (types.includes('face')) {
          setBiometricType('Face ID');
        } else if (types.includes('fingerprint')) {
          setBiometricType('empreinte digitale');
        } else if (types.includes('iris')) {
          setBiometricType('scan iris');
        }
      }
    };
    
    const getDefaultDeviceName = async () => {
      try {
        const info = await Device.getInfo();
        const platform = Capacitor.getPlatform();

        // Set platform-specific defaults
        let defaultName = 'Mon appareil';
        let placeholderText = "Nom de l'appareil";

        if (platform === 'ios') {
          defaultName = 'iPhone';
          placeholderText = 'iPhone de [Votre nom]';
        } else if (platform === 'android') {
          defaultName = 'Android';
          placeholderText = 'Android de [Votre nom]';
        }

        setPlaceholder(placeholderText);

        // Try to get the actual device name
        let name = info?.name;

        // If no name, construct from manufacturer and model
        if (!name || name === 'unknown') {
          const manufacturer = info?.manufacturer || '';
          const model = info?.model || '';

          if (manufacturer && model) {
            // Clean up the name (remove duplicates like "Apple iPhone")
            if (manufacturer.toLowerCase() === 'apple' && model.toLowerCase().includes('iphone')) {
              name = model;
            } else if (model.toLowerCase().includes(manufacturer.toLowerCase())) {
              name = model;
            } else {
              name = `${manufacturer} ${model}`.trim();
            }
          } else if (model) {
            // Just use model if we have it
            name = model;
          } else if (manufacturer) {
            // Just use manufacturer if we have it
            name = manufacturer;
          }
        }

        // Always set a device name - use detected name or fallback to default
        if (name && name !== 'unknown') {
          setDeviceName(name);
        } else {
          setDeviceName(defaultName);
        }
      } catch (error) {
        log.error('Error getting device name:', error);
        const platform = Capacitor.getPlatform();
        // Even on error, set a default name
        if (platform === 'ios') {
          setDeviceName('iPhone');
          setPlaceholder('iPhone de [Votre nom]');
        } else if (platform === 'android') {
          setDeviceName('Android');
          setPlaceholder('Android de [Votre nom]');
        } else {
          setDeviceName('Mon appareil');
          setPlaceholder("Mon appareil");
        }
      }
    };
    
    if (isOpen) {
      checkBiometric();
      getDefaultDeviceName();
    }
  }, [isOpen]);

  log.debug('TrustDeviceModal isOpen:', isOpen);
  if (isOpen === false) return null;

  const handleTrust = async () => {
    setIsLoading(true);
    try {
      await biometricService.enrollTrustedDevice(deviceName);
      if (onTrust) await onTrust(deviceName);
    } catch (error) {
      log.error('Error trusting device:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  return (
    <div className="trust-device-modal-overlay">
      <div className="trust-device-modal">
        <div className="trust-device-header">
          <div className="trust-device-icon">
            <FaShieldAlt size={20} />
          </div>
          <h2>Faire confiance à cet appareil ?</h2>
        </div>

        <div className="trust-device-content">
          <div className="device-info">
            <FaMobileAlt className="device-icon" />
            <input
              type="text"
              className="device-name-input"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder={placeholder || "Nom de l'appareil"}
            autoFocus={false}
            />
          </div>

          <p className="trust-note">
            En faisant confiance à cet appareil, vous pourrez vous connecter 
            rapidement {biometricAvailable ? `avec votre ${biometricType}` : 'de manière sécurisée'}.
          </p>
        </div>

        <div className="trust-device-footer">
          <Button
            type="fixed"
            variant="secondary"
            onClick={handleSkip}
            disabled={isLoading}
          >
            Plus tard
          </Button>
          
          <Button
            type="fixed"
            variant="primary"
            onClick={handleTrust}
            disabled={isLoading || !deviceName.trim()}
            icon={FaShieldAlt}
          >
            {isLoading ? 'Configuration...' : 'Faire confiance'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrustDeviceModal;